import React, { createContext, ReactNode, useState, useEffect } from "react";
import { UserClaims } from "@okta/okta-auth-js";
import {
  Queue,
  useSetActiveQueueForUser,
  useGetActiveQueueForUser,
  useGetQueuesForUser,
  QueueViewState,
} from "@coherehealth/qm-api";
import {
  NURSE_REVIEW_QUEUE_NAME,
  CPP_REVIEW_QUEUE_NAME,
  FAX_INTAKE_QUEUE_NAME,
  MD_REVIEW_QUEUE_NAME,
  P2P_REVIEW_QUEUE_NAME,
  LETTER_WRITING_QUEUE_NAME,
  OUTREACH_QUEUE_NAME,
  NEW_HIRE_AUDIT_REVIEW_QUEUE_NAME,
} from "@coherehealth/common";
import { useSnackbar } from "notistack";

export type User = UserClaims & {
  groups?: Array<string>;
  type?: string /* will be one of the values from UserType */;
  roles?: string /* will be comma separated values from UserType */;
  opsGroup?: string;
};

interface UserContextType {
  getUser: () => Promise<User | undefined>;
  activeQueue: Queue | undefined;
  setActiveQueue: (queueId: string) => Promise<void>;
  queues: Queue[];
  queueView: QueueViewState;
  settingActiveQueue?: boolean;
  gettingActiveQueue?: boolean;
}
export const UserContext = createContext<UserContextType>({
  getUser: () => Promise.reject("No user set"),
  activeQueue: undefined,
  setActiveQueue: async () => {},
  queues: [],
  queueView: "NO_QUEUE",
});

export interface UserProviderProps {
  children: ReactNode;
  getUser: () => Promise<User | undefined>;
  getToken: () => Promise<string | undefined>;
}

export const UserProvider = ({ children, getUser, getToken }: UserProviderProps) => {
  const [activeQueue, setUserActiveQueue] = useState<Queue>();
  const [queueView, setQueueView] = useState<QueueViewState>("NO_QUEUE");
  const { enqueueSnackbar } = useSnackbar();

  const { mutate: mutateActiveQueue, loading: settingActiveQueue } = useSetActiveQueueForUser({
    onMutate: (_, data) => {
      setUserActiveQueue(data);
    },
  });
  const setActiveQueue = async (queueId: string) => {
    const validToken = await getToken();
    if (validToken) {
      await mutateActiveQueue(
        { requestOptions: { headers: { Authorization: `Bearer ${validToken}` } } },
        {
          queryParams: { queueId: queueId },
        }
      );
    }
    return;
  };

  const { data: queues, refetch: getQueues, error: queuesError } = useGetQueuesForUser({ lazy: true });
  const {
    data: userActiveQueue,
    refetch: getActiveQueue,
    loading: gettingActiveQueue,
    error: activeQueueError,
  } = useGetActiveQueueForUser({ lazy: true });
  useEffect(() => {
    let isMounted = true;
    const fetchUserQueueData = async () => {
      let token: string | undefined = undefined;
      if (isMounted) {
        token = await getToken();
      }
      if (token && isMounted) {
        getActiveQueue({ requestOptions: { headers: { Authorization: `Bearer ${token}` } } });
        getQueues({ requestOptions: { headers: { Authorization: `Bearer ${token}` } } });
      }
    };

    fetchUserQueueData();

    return () => {
      isMounted = false;
    };
  }, [getActiveQueue, getQueues, getToken]);
  useEffect(() => {
    if (userActiveQueue) {
      setUserActiveQueue(userActiveQueue);
    }
  }, [userActiveQueue]);

  useEffect(() => {
    const RNQueue = queues?.find((queue) => {
      return queue.name === NURSE_REVIEW_QUEUE_NAME;
    });
    const CPPQueue = queues?.find((queue) => {
      return queue.name === CPP_REVIEW_QUEUE_NAME;
    });
    const FaxQueue = queues?.find((queue) => {
      return queue.name === FAX_INTAKE_QUEUE_NAME;
    });
    const MDQueue = queues?.find((queue) => {
      return queue.name === MD_REVIEW_QUEUE_NAME;
    });
    const P2PQueue = queues?.find((queue) => {
      return queue.name === P2P_REVIEW_QUEUE_NAME;
    });
    const NewHireAuditQueue = queues?.find((queue) => {
      return queue.name === NEW_HIRE_AUDIT_REVIEW_QUEUE_NAME;
    });
    const LetterWritingQueue = queues?.find((queue) => {
      return queue.name === LETTER_WRITING_QUEUE_NAME;
    });
    const OutreachQueue = queues?.find((queue) => {
      return queue.name === OUTREACH_QUEUE_NAME;
    });

    if (activeQueue) {
      if (RNQueue && activeQueue?.id === RNQueue?.id) {
        setQueueView("RN_REVIEW");
      } else if (CPPQueue && activeQueue?.id === CPPQueue?.id) {
        setQueueView("CPP_REVIEW");
      } else if (FaxQueue && activeQueue?.id === FaxQueue?.id) {
        setQueueView("FAX_INTAKE");
      } else if (MDQueue && activeQueue?.id === MDQueue?.id) {
        setQueueView("MD_REVIEW");
      } else if (P2PQueue && activeQueue?.id === P2PQueue?.id) {
        setQueueView("P2P_REVIEW");
      } else if (NewHireAuditQueue && activeQueue?.id === NewHireAuditQueue?.id) {
        setQueueView("NEW_HIRE_AUDIT_REVIEW");
      } else if (LetterWritingQueue && activeQueue?.id === LetterWritingQueue?.id) {
        setQueueView("LETTER_WRITING");
      } else if (OutreachQueue && activeQueue?.id === OutreachQueue?.id) {
        setQueueView("OUTREACH");
      } else {
        if (activeQueue.displayConfig?.view) {
          setQueueView(activeQueue.displayConfig?.view as QueueViewState);
        } else {
          setQueueView("NO_QUEUE");
        }
      }
    } else {
      setQueueView("NO_QUEUE");
    }
  }, [activeQueue, queues]);

  useEffect(() => {
    if (activeQueueError) {
      enqueueSnackbar(`Failed to get active queue: ${activeQueueError?.message}`, { variant: "error" });
    }
  }, [activeQueueError, enqueueSnackbar]);
  useEffect(() => {
    if (queuesError) {
      enqueueSnackbar(`Failed to get user queues: ${queuesError?.message}`, { variant: "error" });
    }
  }, [enqueueSnackbar, queuesError]);

  return (
    <UserContext.Provider
      value={{
        getUser,
        activeQueue,
        setActiveQueue,
        queues: queues || [],
        queueView,
        settingActiveQueue,
        gettingActiveQueue,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
